"use client";

import { useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import {
  createContext,
  type ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import {
  clearSelectedLocation as clearPersistedLocation,
  setSelectedLocation as persistSelectedLocation,
} from "@/actions/selected-location";
import {
  detectCurrentArea,
  getGeolocationPermissionState,
  reverseGeocodeAreaLabel,
} from "@/components/location/auto-location";
import { discoverKeys } from "@/features/discover/queries/keys";
import { hiivesKeys } from "@/features/hiives/queries/keys";
import { useShellProfileData } from "@/hooks/use-shell-profile-data";
import { calculateDistance } from "@/lib/geohash";
import { readSelectedLocationCookie } from "@/lib/locations/selected-location-cookie";
import {
  locationKeyFor,
  type SelectedLocation,
  selectedLocationToContext,
} from "@/lib/locations/types";
import { createClientLogger } from "@/lib/logging/client";
import { getCurrentDevicePosition } from "@/lib/native/current-location";

const logger = createClientLogger("components/location/location-provider");

const RECENT_STORAGE_KEY = "hiive:recent-locations";
const RECENT_LIMIT = 6;

// Auto-detection (Swiggy-style "follow the device"): how far the device must
// be from the active selection before we act, and how often we re-check when
// the app comes back to the foreground.
const AUTO_APPLY_MIN_KM = 1;
const OVERRIDE_PROMPT_MIN_KM = 20;
const FOREGROUND_RECHECK_MS = 15 * 60 * 1000;

// One-time "enable location" nudge for users whose permission state is still
// "prompt" — shown only on the location-driven surfaces.
const NUDGE_STORAGE_KEY = "hiive:location-nudge-shown";
const NUDGE_SURFACES = ["/hiives", "/feed", "/discover"];
const NUDGE_DELAY_MS = 2_500;

interface LocationContextValue {
  /** The location the app currently derives data from. */
  selected: SelectedLocation | null;
  /** The profile baseline used by "reset". */
  profileLocation: SelectedLocation | null;
  /** True when the user has overridden their profile location. */
  isCustom: boolean;
  /**
   * True until the effective location has been resolved (cookie override
   * and/or profile baseline). Consumers should render a skeleton instead of
   * the empty "Set location" affordance while this is true.
   */
  isLoading: boolean;
  recentSearches: SelectedLocation[];
  select: (location: SelectedLocation) => void;
  resetToProfile: () => void;
}

const LocationStateContext = createContext<LocationContextValue | null>(null);

export function LocationProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const queryClient = useQueryClient();

  // Profile baseline ("reset" target) is derived from the shared profile query
  // — the navbar shell already fetches it, so this adds no extra request.
  const { data: profile, isPending: isProfilePending } = useShellProfileData();
  const profileLocation = useMemo<SelectedLocation | null>(() => {
    if (
      profile?.locationLatitude == null ||
      profile?.locationLongitude == null
    ) {
      return null;
    }
    return {
      latitude: profile.locationLatitude,
      longitude: profile.locationLongitude,
      displayName: profile.locationDisplayName ?? "Your location",
      source: "profile",
    };
  }, [
    profile?.locationDisplayName,
    profile?.locationLatitude,
    profile?.locationLongitude,
  ]);

  // The effective selection is resolved client-side (no layout-level server
  // await), so children paint immediately. It starts null and is hydrated from
  // the override cookie / profile baseline after mount — keeping SSR and the
  // first client render identical (no hydration mismatch). `hasExplicitSelection`
  // guards a user's override/pick from being clobbered by a later profile load.
  const [selected, setSelected] = useState<SelectedLocation | null>(null);
  const hasExplicitSelection = useRef(false);
  // Cookie read only happens after mount; until then we must not flash
  // "Set location" as if the user had no location.
  const [hasHydratedCookie, setHasHydratedCookie] = useState(false);

  const [recentSearches, setRecentSearches] = useState<SelectedLocation[]>([]);

  // Hydrate the override from the (non-httpOnly) cookie after mount. It wins
  // over the profile baseline, mirroring server-side getEffectiveLocation.
  useEffect(() => {
    const override = readSelectedLocationCookie();
    if (override) {
      hasExplicitSelection.current = true;
      setSelected(override);
    }
    setHasHydratedCookie(true);
  }, []);

  // Recent searches live client-side only.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(RECENT_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setRecentSearches(parsed.slice(0, RECENT_LIMIT));
      }
    } catch (error) {
      logger.warn("Failed to read recent locations", error);
    }
  }, []);

  // Keep the hiives location cache in sync so its React Query–driven sections
  // refetch instantly, without waiting on the RSC refresh.
  const syncHiivesCache = useCallback(
    (location: SelectedLocation | null) => {
      queryClient.setQueryData(hiivesKeys.location(), {
        locationContext: selectedLocationToContext(location),
        locationKey: locationKeyFor(location),
      });
    },
    [queryClient],
  );

  // When there's no override cookie and the user hasn't picked anything, follow
  // the profile baseline as soon as it resolves.
  useEffect(() => {
    if (!hasExplicitSelection.current && profileLocation) {
      setSelected(profileLocation);
    }
  }, [profileLocation]);

  // Align the hiives location cache with the effective selection on mount and
  // whenever it changes. This prevents a stale persisted (IndexedDB) location
  // from disagreeing with the chip / cookie.
  useEffect(() => {
    syncHiivesCache(selected);
  }, [selected, syncHiivesCache]);

  // After the cookie is persisted, force the other location-derived client
  // queries to refetch (the feed sessions key starts with "activitySessions";
  // discover buddies live under discoverKeys.all). Hiives is handled instantly
  // by syncHiivesCache above.
  const invalidateLocationQueries = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["activitySessions"] });
    void queryClient.invalidateQueries({ queryKey: discoverKeys.all });
  }, [queryClient]);

  const pushRecent = useCallback((location: SelectedLocation) => {
    setRecentSearches((previous) => {
      const next = [
        location,
        ...previous.filter((item) => item.displayName !== location.displayName),
      ].slice(0, RECENT_LIMIT);
      try {
        window.localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(next));
      } catch (error) {
        logger.warn("Failed to persist recent locations", error);
      }
      return next;
    });
  }, []);

  const select = useCallback(
    (location: SelectedLocation) => {
      hasExplicitSelection.current = true;
      setSelected(location);
      pushRecent(location);
      void persistSelectedLocation(location)
        .then(() => {
          invalidateLocationQueries();
          router.refresh();
        })
        .catch((error) =>
          logger.error("Failed to persist selected location", error),
        );
    },
    [invalidateLocationQueries, pushRecent, router],
  );

  // ── Swiggy-style auto-detection ─────────────────────────────────────────
  // When the device has ALREADY granted geolocation (no prompting), follow it:
  // refresh the effective location on launch and on foreground resume so users
  // who moved areas stop seeing results for wherever they last set the chip.
  // An explicit custom pick is respected — we only offer a switch via toast.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const profileLocationRef = useRef(profileLocation);
  profileLocationRef.current = profileLocation;
  const autoDetectInFlightRef = useRef(false);
  const lastAutoDetectAtRef = useRef(0);
  const overridePromptShownRef = useRef(false);

  // Applies without touching recent searches — detected locations are
  // ambient, not something the user picked.
  const applyDetectedLocation = useCallback(
    (location: SelectedLocation) => {
      hasExplicitSelection.current = true;
      setSelected(location);
      void persistSelectedLocation(location)
        .then(() => {
          invalidateLocationQueries();
          router.refresh();
        })
        .catch((error) =>
          logger.error("Failed to persist detected location", error),
        );
    },
    [invalidateLocationQueries, router],
  );

  const runAutoDetect = useCallback(async () => {
    if (autoDetectInFlightRef.current) return;
    autoDetectInFlightRef.current = true;
    lastAutoDetectAtRef.current = Date.now();
    try {
      const detected = await detectCurrentArea();
      if (!detected) return;

      const override = readSelectedLocationCookie();
      if (override?.source === "custom") {
        // The user deliberately browses somewhere else — never clobber it,
        // but let them jump to where they actually are when it's far away.
        const driftKm = calculateDistance(
          override.latitude,
          override.longitude,
          detected.latitude,
          detected.longitude,
        );
        if (
          driftKm >= OVERRIDE_PROMPT_MIN_KM &&
          !overridePromptShownRef.current
        ) {
          overridePromptShownRef.current = true;
          toast(`You seem to be near ${detected.displayName}`, {
            description: `Showing results for ${override.displayName.split(",")[0]}.`,
            duration: 8_000,
            action: {
              label: "Switch",
              onClick: () => applyDetectedLocation(detected),
            },
          });
        }
        return;
      }

      // Following the device (stale "current" cookie) or the profile
      // baseline: refresh silently, but skip the cookie+RSC churn when the
      // effective location is already accurate.
      const baseline =
        override ?? selectedRef.current ?? profileLocationRef.current;
      if (
        baseline &&
        calculateDistance(
          baseline.latitude,
          baseline.longitude,
          detected.latitude,
          detected.longitude,
        ) < AUTO_APPLY_MIN_KM
      ) {
        return;
      }
      applyDetectedLocation(detected);
    } finally {
      autoDetectInFlightRef.current = false;
    }
  }, [applyDetectedLocation]);

  // Launch check — after the override cookie has been read, so the policy
  // sees the real selection state.
  const initialAutoDetectRanRef = useRef(false);
  useEffect(() => {
    if (!hasHydratedCookie || initialAutoDetectRanRef.current) return;
    initialAutoDetectRanRef.current = true;
    void runAutoDetect();
  }, [hasHydratedCookie, runAutoDetect]);

  // Foreground resume — the native webview stays alive for days, so launch
  // checks alone never catch users moving across town with the app open.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!initialAutoDetectRanRef.current) return;
      if (Date.now() - lastAutoDetectAtRef.current < FOREGROUND_RECHECK_MS) {
        return;
      }
      void runAutoDetect();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [runAutoDetect]);

  const resetToProfile = useCallback(() => {
    hasExplicitSelection.current = false;
    setSelected(profileLocation);
    void clearPersistedLocation()
      .then(() => {
        invalidateLocationQueries();
        router.refresh();
      })
      .catch((error) =>
        logger.error("Failed to clear selected location", error),
      );
  }, [invalidateLocationQueries, profileLocation, router]);

  // Still resolving when the cookie hasn't been read yet, the profile query
  // hasn't settled, or the profile baseline exists but hasn't been copied
  // into `selected` yet (one-frame gap after the query resolves).
  const isLoading =
    !hasHydratedCookie ||
    (selected == null && (isProfilePending || profileLocation != null));

  const value = useMemo<LocationContextValue>(
    () => ({
      selected,
      profileLocation,
      isCustom: selected != null && selected.source !== "profile",
      isLoading,
      recentSearches,
      select,
      resetToProfile,
    }),
    [
      isLoading,
      profileLocation,
      recentSearches,
      resetToProfile,
      select,
      selected,
    ],
  );

  return (
    <LocationStateContext.Provider value={value}>
      {children}
      {/*
        usePathname suspends on dynamic routes under Cache Components when
        params aren't known at build time. Keep that read out of the provider
        tree so children can still prerender a static shell.
      */}
      <Suspense fallback={null}>
        <LocationEnableNudge
          hasHydratedCookie={hasHydratedCookie}
          applyDetectedLocation={applyDetectedLocation}
        />
      </Suspense>
    </LocationStateContext.Provider>
  );
}

/**
 * Pathname-gated one-time "enable location" toast. Isolated so usePathname
 * can suspend behind Suspense without blocking LocationProvider consumers.
 */
function LocationEnableNudge({
  hasHydratedCookie,
  applyDetectedLocation,
}: {
  hasHydratedCookie: boolean;
  applyDetectedLocation: (location: SelectedLocation) => void;
}) {
  const pathname = usePathname();

  const enableLocationFromNudge = useCallback(async () => {
    try {
      const position = await getCurrentDevicePosition({
        enableHighAccuracy: false,
        timeout: 15_000,
        maximumAge: 60_000,
      });
      const detected: SelectedLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        displayName: await reverseGeocodeAreaLabel(
          position.coords.latitude,
          position.coords.longitude,
        ),
        source: "current",
      };
      applyDetectedLocation(detected);
      toast.success(`Showing what's near ${detected.displayName}`);
    } catch (error) {
      // Declined the prompt (or the read failed) — stay on the fallback.
      logger.warn("Enable-location nudge request failed", error);
    }
  }, [applyDetectedLocation]);

  useEffect(() => {
    if (!hasHydratedCookie) return;
    // Only the three discovery landing pages own this prompt. Nested event,
    // booking, or profile routes should never interrupt the task in progress.
    const onNudgeSurface = NUDGE_SURFACES.includes(pathname);
    if (!onNudgeSurface) return;
    try {
      if (window.localStorage.getItem(NUDGE_STORAGE_KEY)) return;
    } catch {
      return;
    }
    // A deliberate override means they've already engaged with location.
    if (readSelectedLocationCookie()) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      const state = await getGeolocationPermissionState();
      if (cancelled || state !== "prompt") return;
      try {
        window.localStorage.setItem(NUDGE_STORAGE_KEY, "1");
      } catch {
        return;
      }
      // Neutral, non-urging copy with a "Continue" action. App Store guideline
      // 5.1.1(iv) treats a pre-prompt that encourages the grant — or whose
      // button says "Enable" / "Allow" — as directing the user's answer, so this
      // only explains what location is used for and hands the decision to the
      // system prompt.
      toast("Hiive can use your location", {
        description:
          "Location is used to show events, venues and buddies near you. Hiive works without it — you can also set your area manually.",
        duration: 15_000,
        style: {
          minHeight: "92px",
          gap: "12px",
          padding: "16px 18px",
          borderRadius: "18px",
        },
        classNames: {
          content: "min-w-0 flex-1 gap-1",
          title: "font-bold text-[15px] leading-5",
          description: "text-[13px] leading-5",
        },
        actionButtonStyle: {
          minWidth: "80px",
          height: "44px",
          padding: "0 16px",
          borderRadius: "9999px",
          fontSize: "0.875rem",
          fontWeight: 700,
        },
        action: {
          label: "Continue",
          onClick: () => void enableLocationFromNudge(),
        },
      });
    }, NUDGE_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [hasHydratedCookie, pathname, enableLocationFromNudge]);

  return null;
}

export function useSelectedLocation(): LocationContextValue {
  const context = useContext(LocationStateContext);
  if (!context) {
    throw new Error(
      "useSelectedLocation must be used within a LocationProvider",
    );
  }
  return context;
}

/**
 * For surfaces that also render outside the AppShell (the `(without-navbar)`
 * hiive page): null when there is no provider, so the caller falls back to
 * its server-resolved location instead of throwing.
 */
export function useOptionalSelectedLocation(): LocationContextValue | null {
  return useContext(LocationStateContext);
}
